import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import SEO from "@/components/SEO";
import { StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { SectionHeader } from "@/components/AnimatedText";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Bed, Plane, Store, Sparkles, Trophy, Users } from "lucide-react";
import venueImage from "@/assets/convention-center.webp";
import hotelImage from "@/assets/hotel.webp";
import airportImage from "@/assets/airport.webp";
import winnersCupLogo from "@/assets/winners-cup-logo.png";
import PreRegistrationForm from "@/components/PreRegistrationForm";
import RegistrationPricing from "@/components/RegistrationPricing";

const sections = [
  { icon: MapPin, title: "Venue", description: "Ontario Convention Center — 100,000+ sq ft of competition space", href: "/venue", image: venueImage },
  { icon: Bed, title: "Hotel", description: "Delta Hotels Ontario Airport — Steps from the venue", href: "/hotel", image: hotelImage },
  { icon: Plane, title: "Travel", description: "Ontario International Airport — Just 5 minutes away", href: "/travel", image: airportImage },
  { icon: Store, title: "Vendors", description: "Exhibitors and partners — Showcase your brand", href: "/vendors", image: null },
];

const highlights = [
  { icon: Trophy, title: "World-Class Competition", description: "Athletes compete across multiple disciplines in a state-of-the-art 100,000+ sq ft facility." },
  { icon: Sparkles, title: "3 Days of Excellence", description: "February 26–28, 2027 packed with competitions, ceremonies, and unforgettable moments." },
  { icon: Users, title: "Southern California", description: "Located in Ontario, CA — minutes from the airport and surrounded by world-famous attractions." },
];

const Index = () => {
  return (
    <PageTransition>
      <SEO type="event" image="/og-image.png" url="https://winnerscup.com" />
      <main className="overflow-x-hidden">
        <Navbar />
        <h1 className="sr-only">Winner's Cup 2027 — Premier West Coast Gymnastics Competition</h1>
        <Hero />

        {/* Branded Stats Strip */}
        <section className="relative bg-secondary border-y border-border/60 overflow-hidden">
          <div className="absolute inset-0 opacity-30 bg-gradient-mesh pointer-events-none" />
          <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-14 relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-10 items-center">
              {[
                { value: "3", label: "Days of Competition" },
                { value: "100K+", label: "Sq Ft of Space" },
                { value: "1", label: "Premier Venue" },
                { value: "∞", label: "Unforgettable Moments" },
              ].map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="text-center"
                >
                  <div className="font-display text-3xl sm:text-5xl text-gradient leading-none">{s.value}</div>
                  <div className="mt-2 text-muted-foreground text-[11px] sm:text-xs tracking-[0.2em] uppercase">{s.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What to Expect */}
        <section className="py-24 sm:py-32 bg-background relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-mesh opacity-40" />
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <SectionHeader badge="What to Expect" title="THE PREMIER GYMNASTICS EVENT" className="mb-16 sm:mb-20" />

            <div className="grid md:grid-cols-3 gap-6">
              {highlights.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative bg-card border border-border rounded-2xl p-8 hover:border-primary/20 transition-all duration-500"
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-gradient-coral rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <item.icon className="w-6 h-6 text-foreground" />
                    </div>
                    <h3 className="font-display text-xl text-foreground mb-3">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed text-sm">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Banner — dark cinematic break */}
        <section className="relative bg-dark text-white py-20 sm:py-28 overflow-hidden scanlines">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-2xl" style={{ background: "radial-gradient(circle, hsl(28 100% 58% / 0.35) 0%, transparent 60%)" }} />
            <div className="absolute -bottom-56 -right-40 w-[720px] h-[720px] rounded-full blur-2xl" style={{ background: "radial-gradient(circle, hsl(330 92% 58% / 0.28) 0%, transparent 65%)" }} />
            <div className="absolute top-1/2 -left-40 -translate-y-1/2 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, hsl(252 75% 60% / 0.22) 0%, transparent 70%)" }} />
            <div className="absolute inset-0 grid-bg opacity-25" />
          </div>
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-sunset" />
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-sunset" />
          <div className="container mx-auto px-6 relative z-10 text-center max-w-3xl">
            <motion.img
              src={winnersCupLogo}
              alt="Winner's Cup"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="w-32 sm:w-44 mx-auto mb-8"
              style={{ filter: "drop-shadow(0 0 24px hsl(18 100% 60% / 0.55)) drop-shadow(0 0 48px hsl(330 92% 58% / 0.35))" }}
            />
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.7 }}
              className="font-display text-3xl sm:text-5xl text-white tracking-tight"
            >
              Where champions <span className="text-gradient">are made.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.25 }}
              className="mt-5 text-white/75 text-base sm:text-lg leading-relaxed"
            >
              For over a decade, Winner's Gymnastics has built one of the most respected competitions on the West Coast. The 2027 edition raises the bar.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="mt-8 flex items-center justify-center gap-3 text-white/60 text-xs tracking-[0.25em] uppercase"
            >
              <span className="h-px w-10 bg-white/20" />
              Hosted by Winner's Gymnastics
              <span className="h-px w-10 bg-white/20" />
            </motion.div>
          </div>
        </section>
        
        {/* Explore Section */}
        <section className="py-20 sm:py-28 bg-background relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/15 to-transparent" />
          <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full" style={{ background: "radial-gradient(circle, hsl(15 100% 55% / 0.04) 0%, transparent 70%)" }} />
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <SectionHeader badge="Explore" title="PLAN YOUR EXPERIENCE" className="mb-12 sm:mb-16 [&_h2]:text-foreground" />

            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" staggerDelay={0.12}>
              {sections.map((section) => (
                <StaggerItem key={section.title} animation="fade-up">
                  <Link to={section.href} className="block group">
                    <motion.div
                      className="relative h-56 sm:h-64 lg:h-72 rounded-2xl overflow-hidden border border-border/60"
                      whileHover={{ scale: 1.02 }}
                      transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    >
                      {section.image ? (
                        <>
                          <motion.img src={section.image} alt={section.title} loading="lazy" decoding="async" width={1200} height={800} className="w-full h-full object-cover" whileHover={{ scale: 1.08 }} transition={{ duration: 0.8 }} />
                          <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/70 to-transparent" />
                        </>
                      ) : (
                        <div className="absolute inset-0 bg-gradient-sunset" />
                      )}
                      
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-primary/20 to-transparent" />
                      
                      <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-end">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-coral rounded-xl flex items-center justify-center shadow-glow group-hover:scale-110 transition-transform">
                            <section.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                          </div>
                          <h3 className="font-display text-2xl sm:text-3xl text-white">{section.title}</h3>
                        </div>
                        <p className="text-white/85 text-sm sm:text-base mb-4 max-w-sm">{section.description}</p>
                        <div className="flex items-center gap-2 text-primary-foreground font-semibold text-xs uppercase tracking-wider">
                          <span>Explore</span>
                          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Pre-Registration */}
        <section id="pre-register" className="py-24 sm:py-28 bg-dark relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-mesh opacity-40" />
          <div className="absolute inset-0 bg-spotlight" />
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <SectionHeader badge="Team Pre-Registration" title="RESERVE YOUR SPOT" className="mb-10 [&_h2]:heading-sunset" />
            <p className="text-white/70 text-center max-w-xl mx-auto -mt-4 mb-10">
              Submit your team details below. We'll follow up with full registration details once it opens.
            </p>
            <RegistrationPricing />
            <PreRegistrationForm />
          </div>
        </section>

        <Footer />
      </main>
    </PageTransition>
  );
};

export default Index;
